import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiSearch, FiPlus, FiEdit2, FiTrash2, FiX, FiDownload,
  FiUsers, FiSave, FiEye,
  FiAlertTriangle, FiChevronLeft, FiChevronRight, FiUpload
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import { contactAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const CONTACT_TYPES = ['Buyer', 'Supplier', 'Agent', 'Other'];

const emptyContact = {
  contact_type: 'Buyer',
  name: '',
  deals_in: '',
  address: '',
  country: '',
  phone: '',
  email: '',
  fax: '',
  gstin: '',
  tin: '',
  cst_no: '',
  bank_account_no: '',
  bank_name: '',
  bank_ifsc: '',
  remarks: '',
};

// ── Styles ──────────────────────────────────────────────────────────────────────

const styles = {
  container: {
    fontFamily: 'Inter, sans-serif',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 28,
    flexWrap: 'wrap',
    gap: 16,
  },
  title: {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 28,
    fontWeight: 800,
    color: '#064e3b',
    margin: 0,
  },
  subtitle: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 4,
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  searchWrapper: {
    position: 'relative',
    flex: 1,
    minWidth: 250,
  },
  searchIcon: {
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#059669',
  },
  searchInput: {
    width: '100%',
    padding: '12px 14px 12px 44px',
    border: '2px solid rgba(16, 185, 129, 0.2)',
    borderRadius: 12,
    fontSize: 14,
    outline: 'none',
    background: 'rgba(255,255,255,0.8)',
    backdropFilter: 'blur(8px)',
    transition: 'all 0.2s ease',
    fontFamily: 'Inter, sans-serif',
  },
  addBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #059669, #10b981)',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
    fontFamily: 'Poppins, sans-serif',
    whiteSpace: 'nowrap',
  },
  glassCard: {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    borderRadius: 20,
    border: '1px solid rgba(255, 255, 255, 0.5)',
    boxShadow: '0 8px 32px rgba(6, 78, 59, 0.08)',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  th: {
    padding: '14px 16px',
    textAlign: 'left',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    color: '#047857',
    background: 'rgba(240, 253, 244, 0.8)',
    borderBottom: '2px solid rgba(16, 185, 129, 0.15)',
  },
  td: {
    padding: '14px 16px',
    fontSize: 14,
    color: '#1f2937',
    borderBottom: '1px solid rgba(16, 185, 129, 0.08)',
    verticalAlign: 'middle',
  },
  actionBtn: {
    width: 34,
    height: 34,
    borderRadius: 8,
    border: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    marginRight: 6,
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(6, 78, 59, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'white',
    borderRadius: 24,
    padding: '32px',
    width: '95%',
    maxWidth: 780,
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 25px 80px rgba(0, 0, 0, 0.2)',
    position: 'relative',
  },
  modalTitle: {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 22,
    fontWeight: 700,
    color: '#064e3b',
    marginBottom: 24,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 16,
  },
  fieldLabel: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#047857',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  fieldInput: {
    width: '100%',
    padding: '10px 14px',
    border: '2px solid rgba(16, 185, 129, 0.2)',
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    transition: 'all 0.2s ease',
    background: '#f0fdf4',
    fontFamily: 'Inter, sans-serif',
    boxSizing: 'border-box',
  },
  fieldSelect: {
    width: '100%',
    padding: '10px 14px',
    border: '2px solid rgba(16, 185, 129, 0.2)',
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    background: '#f0fdf4',
    fontFamily: 'Inter, sans-serif',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  fieldTextarea: {
    width: '100%',
    padding: '10px 14px',
    border: '2px solid rgba(16, 185, 129, 0.2)',
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    background: '#f0fdf4',
    fontFamily: 'Inter, sans-serif',
    resize: 'vertical',
    minHeight: 60,
    boxSizing: 'border-box',
  },
  saveBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 32px',
    background: 'linear-gradient(135deg, #059669, #10b981)',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
    fontFamily: 'Poppins, sans-serif',
  },
  cancelBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 24px',
    background: 'transparent',
    color: '#6b7280',
    border: '2px solid #e5e7eb',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'Poppins, sans-serif',
  },
  badge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#9ca3af',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '16px 0',
  },
  pageBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: '2px solid rgba(16, 185, 129, 0.2)',
    background: 'white',
    color: '#047857',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  },
  pageBtnActive: {
    background: 'linear-gradient(135deg, #059669, #10b981)',
    color: 'white',
    border: '2px solid #10b981',
  },
};

const typeColors = {
  Buyer: { bg: '#dcfce7', color: '#166534' },
  Supplier: { bg: '#dbeafe', color: '#1e40af' },
  Agent: { bg: '#fef9c3', color: '#854d0e' },
  Other: { bg: '#f3e8ff', color: '#6b21a8' },
};

// ── Helper: Export single contact to PDF ────────────────────────────────────────

function exportContactPDF(contact) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header
  doc.setFillColor(6, 78, 59);
  doc.rect(0, 0, pageWidth, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('CONTACT DETAILS', pageWidth / 2, 18, { align: 'center' });
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Export Date: ${new Date().toLocaleDateString('en-GB')}`, pageWidth / 2, 30, { align: 'center' });

  // Contact name
  doc.setTextColor(6, 78, 59);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(contact.name || 'N/A', 20, 55);

  // Type badge
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`Type: ${contact.contact_type || 'N/A'}`, 20, 63);

  // Separator
  doc.setDrawColor(16, 185, 129);
  doc.setLineWidth(0.5);
  doc.line(20, 68, pageWidth - 20, 68);

  // Detail fields
  const fields = [
    ['Deals In', contact.deals_in],
    ['Address', contact.address],
    ['Country', contact.country],
    ['Phone', contact.phone],
    ['Email', contact.email],
    ['Fax', contact.fax],
    ['GSTIN', contact.gstin],
    ['TIN', contact.tin],
    ['CST No', contact.cst_no],
    ['Bank Account No', contact.bank_account_no],
    ['Bank Name', contact.bank_name],
    ['Bank IFSC', contact.bank_ifsc],
    ['Remarks', contact.remarks],
  ];

  let yPos = 78;
  doc.setFontSize(11);

  fields.forEach(([label, value]) => {
    if (yPos > 270) {
      doc.addPage();
      yPos = 20;
    }
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(4, 120, 87);
    doc.text(`${label}:`, 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(50, 50, 50);
    const val = value || 'N/A';
    const lines = doc.splitTextToSize(val, pageWidth - 80);
    doc.text(lines, 80, yPos);
    yPos += Math.max(lines.length * 6, 8) + 4;
  });

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }

  doc.save(`Contact_${(contact.name || 'Unknown').replace(/\s+/g, '_')}.pdf`);
}

// ── Helper: Preview contact PDF in modal ────────────────────────────────────────

function generateContactPDFBlob(contact) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header
  doc.setFillColor(6, 78, 59);
  doc.rect(0, 0, pageWidth, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('CONTACT DETAILS', pageWidth / 2, 18, { align: 'center' });
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Export Date: ${new Date().toLocaleDateString('en-GB')}`, pageWidth / 2, 30, { align: 'center' });

  // Contact name
  doc.setTextColor(6, 78, 59);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(contact.name || 'N/A', 20, 55);

  // Type badge
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`Type: ${contact.contact_type || 'N/A'}`, 20, 63);

  // Separator
  doc.setDrawColor(16, 185, 129);
  doc.setLineWidth(0.5);
  doc.line(20, 68, pageWidth - 20, 68);

  // Detail fields
  const fields = [
    ['Deals In', contact.deals_in],
    ['Address', contact.address],
    ['Country', contact.country],
    ['Phone', contact.phone],
    ['Email', contact.email],
    ['Fax', contact.fax],
    ['GSTIN', contact.gstin],
    ['TIN', contact.tin],
    ['CST No', contact.cst_no],
    ['Bank Account No', contact.bank_account_no],
    ['Bank Name', contact.bank_name],
    ['Bank IFSC', contact.bank_ifsc],
    ['Remarks', contact.remarks],
  ];

  let yPos = 78;
  doc.setFontSize(11);

  fields.forEach(([label, value]) => {
    if (yPos > 270) {
      doc.addPage();
      yPos = 20;
    }
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(4, 120, 87);
    doc.text(`${label}:`, 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(50, 50, 50);
    const val = value || 'N/A';
    const lines = doc.splitTextToSize(val, pageWidth - 80);
    doc.text(lines, 80, yPos);
    yPos += Math.max(lines.length * 6, 8) + 4;
  });

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }

  return doc.output('blob');
}

// ── Component ───────────────────────────────────────────────────────────────────

export default function ContactManagement() {
  const { activeCompanyId, activeCompany } = useAuth();

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [form, setForm] = useState({ ...emptyContact });
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [importModal, setImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const perPage = 12;

  // ── Clear + refetch contacts on company switch ─────────────────────────────

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await contactAPI.getAll();
      setContacts(res.data?.contacts || res.data || []);
    } catch (err) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId]);

  useEffect(() => {
    setContacts([]);
    setPage(1);
    fetchContacts();
  }, [fetchContacts]);

  // ── Filtering ────────────────────────────────────────────────────────────────

  const filtered = contacts.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (c.contact_type || '').toLowerCase().includes(q) ||
      (c.deals_in || '').toLowerCase().includes(q) ||
      (c.address || '').toLowerCase().includes(q) ||
      (c.phone || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  useEffect(() => {
    setPage(1);
  }, [search]);

  // ── Modal handlers ──────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditingContact(null);
    setForm({ ...emptyContact });
    setModalOpen(true);
  };

  const openEdit = (contact) => {
    setEditingContact(contact);
    setForm({
      contact_type: contact.contact_type || 'Buyer',
      name: contact.name || '',
      deals_in: contact.deals_in || '',
      address: contact.address || '',
      country: contact.country || '',
      phone: contact.phone || '',
      email: contact.email || '',
      fax: contact.fax || '',
      gstin: contact.gstin || '',
      tin: contact.tin || '',
      cst_no: contact.cst_no || '',
      bank_account_no: contact.bank_account_no || '',
      bank_name: contact.bank_name || '',
      bank_ifsc: contact.bank_ifsc || '',
      remarks: contact.remarks || '',
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingContact(null);
    setForm({ ...emptyContact });
  };

  // ── Save ────────────────────────────────────────────────────────────────────

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Contact name is required');
      return;
    }
    setSaving(true);
    try {
      if (editingContact) {
        await contactAPI.update(editingContact.id, form);
        toast.success('Contact updated successfully');
      } else {
        await contactAPI.create(form);
        toast.success('Contact created successfully');
      }
      closeModal();
      fetchContacts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      await contactAPI.delete(deleteModal.id);
      toast.success('Contact deleted');
      setDeleteModal(null);
      fetchContacts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete contact');
    }
  };

  // ── PDF Preview ──────────────────────────────────────────────────────────────

  const handlePdfPreview = (contact) => {
    try {
      const pdfBlob = generateContactPDFBlob(contact);
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfPreview({ contact, url: pdfUrl });
    } catch (err) {
      toast.error('Failed to generate PDF preview');
      console.error(err);
    }
  };

  const closePdfPreview = () => {
    if (pdfPreview?.url) {
      URL.revokeObjectURL(pdfPreview.url);
    }
    setPdfPreview(null);
  };

  // ── Field change helper ──────────────────────────────────────────────────────

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // ── CSV Import ──────────────────────────────────────────────────────────────

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setImportFile(file);
    } else {
      toast.error('Please select a valid CSV file');
      e.target.value = '';
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setImporting(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', importFile);

    try {
      const response = await contactAPI.importCSV(formData, (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
      });
      toast.success(
        `Import completed! ${response.data.success_count} contacts imported successfully`
      );
      if (response.data.error_count > 0) {
        toast.warning(`${response.data.error_count} contacts failed to import`);
      }
      setImportModal(false);
      setImportFile(null);
      setUploadProgress(0);
      fetchContacts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to import contacts');
      setUploadProgress(0);
    } finally {
      setImporting(false);
    }
  };

  const downloadDemoCSV = () => {
    const demoData = [
      ['name', 'deals in', 'address', 'phone', 'email', 'remarks'],
      ['Acme Corporation', 'Cotton Exporter', '123 Main Street, Mumbai, Maharashtra 400001', '+91-9876543210', 'contact@acme.com', 'GSTIN:27AABCA1234E1ZX, Bank: HDFC Bank, A/C NO: 1234567890, IFSC CODE: HDFC0001234'],
      ['Global Traders Ltd', 'Import Agent', '456 Park Avenue, Delhi 110001', '+91-9123456789', 'info@globaltraders.com', 'C&F Agent for North India'],
      ['Tech Suppliers Pvt Ltd', 'Electronics Supplier', '789 Tech Park, Bangalore, Karnataka 560001', '+91-9988776655', 'sales@techsuppliers.com', 'GSTIN:29AABCT5678F1ZY, FAX: 080-12345678'],
      ['Ocean Exports', 'Seafood Exporter', '321 Marine Drive, Kochi, Kerala 682001', '+91-9567891234', 'exports@ocean.com', 'Bank: State Bank of India, A/C NO: 9876543210, IFSC: SBIN0012345'],
      ['Metro Logistics', 'Transport & C&F Agent', '654 Industrial Area, Pune, Maharashtra 411001', '+91-9345678901', 'logistics@metro.com', 'All India Transport Service'],
    ];

    const csvContent = demoData.map(row =>
      row.map(cell => {
        // Escape quotes and wrap in quotes if contains comma, newline, or quote
        if (cell.includes(',') || cell.includes('\n') || cell.includes('"')) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
      }).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'contacts_demo_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Demo CSV template downloaded!');
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={styles.container}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={styles.header}
      >
        <div>
          <h1 style={styles.title}>Contacts</h1>
          <p style={styles.subtitle}>
            {activeCompany?.exporter_name
              ? <><span style={{ fontWeight: 600, color: '#065f46' }}>{activeCompany.exporter_name}</span> &mdash; Manage buyers, suppliers, agents and other contacts</>
              : 'Manage buyers, suppliers, agents and other contacts'
            }
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            style={{
              ...styles.addBtn,
              background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
              boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
            }}
            onClick={() => setImportModal(true)}
          >
            <FiUpload size={18} />
            Import CSV
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            style={styles.addBtn}
            onClick={openCreate}
          >
            <FiPlus size={18} />
            Add Contact
          </motion.button>
        </div>
      </motion.div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        style={styles.searchRow}
      >
        <div style={styles.searchWrapper}>
          <FiSearch size={18} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search contacts by name, type, deals, address, phone, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
            onFocus={(e) => {
              e.target.style.borderColor = '#10b981';
              e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.12)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(16, 185, 129, 0.2)';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
        <div style={{ fontSize: 13, color: '#6b7280', whiteSpace: 'nowrap' }}>
          {filtered.length} contact{filtered.length !== 1 ? 's' : ''}
        </div>
      </motion.div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.15 }}
        style={styles.glassCard}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div
              style={{
                width: 40,
                height: 40,
                border: '3px solid rgba(16, 185, 129, 0.2)',
                borderTopColor: '#10b981',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                margin: '0 auto 16px',
              }}
            />
            <p style={{ color: '#6b7280', fontSize: 14 }}>Loading contacts...</p>
          </div>
        ) : paginated.length === 0 ? (
          <div style={styles.emptyState}>
            <FiUsers size={48} style={{ marginBottom: 16, opacity: 0.4 }} />
            <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
              {search ? 'No contacts match your search' : 'No contacts yet'}
            </p>
            <p style={{ fontSize: 13 }}>
              {search
                ? 'Try adjusting your search query'
                : 'Click "Add Contact" to create your first contact'}
            </p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Contact Type</th>
                    <th style={styles.th}>Deals In</th>
                    <th style={styles.th}>Address</th>
                    <th style={styles.th}>Phone</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((contact, idx) => (
                    <motion.tr
                      key={contact.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      style={{ cursor: 'default' }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = 'rgba(240, 253, 244, 0.5)')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = 'transparent')
                      }
                    >
                      <td style={{ ...styles.td, fontWeight: 600, color: '#064e3b' }}>
                        {contact.name}
                      </td>
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.badge,
                            background:
                              typeColors[contact.contact_type]?.bg || '#f3f4f6',
                            color:
                              typeColors[contact.contact_type]?.color || '#374151',
                          }}
                        >
                          {contact.contact_type}
                        </span>
                      </td>
                      <td style={styles.td}>{contact.deals_in || '-'}</td>
                      <td style={{ ...styles.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {contact.address || '-'}
                      </td>
                      <td style={styles.td}>{contact.phone || '-'}</td>
                      <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <button
                          title="Edit"
                          onClick={() => openEdit(contact)}
                          style={{
                            ...styles.actionBtn,
                            background: 'rgba(16, 185, 129, 0.1)',
                            color: '#059669',
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background =
                              'rgba(16, 185, 129, 0.2)')
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background =
                              'rgba(16, 185, 129, 0.1)')
                          }
                        >
                          <FiEdit2 size={15} />
                        </button>
                        <button
                          title="View PDF"
                          onClick={() => handlePdfPreview(contact)}
                          style={{
                            ...styles.actionBtn,
                            background: 'rgba(168, 85, 247, 0.1)',
                            color: '#a855f7',
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background =
                              'rgba(168, 85, 247, 0.2)')
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background =
                              'rgba(168, 85, 247, 0.1)')
                          }
                        >
                          <FiEye size={15} />
                        </button>
                        <button
                          title="Export PDF"
                          onClick={() => exportContactPDF(contact)}
                          style={{
                            ...styles.actionBtn,
                            background: 'rgba(59, 130, 246, 0.1)',
                            color: '#2563eb',
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background =
                              'rgba(59, 130, 246, 0.2)')
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background =
                              'rgba(59, 130, 246, 0.1)')
                          }
                        >
                          <FiDownload size={15} />
                        </button>
                        <button
                          title="Delete"
                          onClick={() => setDeleteModal(contact)}
                          style={{
                            ...styles.actionBtn,
                            background: 'rgba(239, 68, 68, 0.1)',
                            color: '#dc2626',
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background =
                              'rgba(239, 68, 68, 0.2)')
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background =
                              'rgba(239, 68, 68, 0.1)')
                          }
                        >
                          <FiTrash2 size={15} />
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={styles.pagination}>
                <button
                  style={{
                    ...styles.pageBtn,
                    opacity: page === 1 ? 0.4 : 1,
                    cursor: page === 1 ? 'default' : 'pointer',
                  }}
                  onClick={() => page > 1 && setPage(page - 1)}
                  disabled={page === 1}
                >
                  <FiChevronLeft size={16} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    (p) =>
                      p === 1 ||
                      p === totalPages ||
                      Math.abs(p - page) <= 2
                  )
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) {
                      acc.push('...');
                    }
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    p === '...' ? (
                      <span key={`ellipsis-${idx}`} style={{ color: '#9ca3af', padding: '0 4px' }}>
                        ...
                      </span>
                    ) : (
                      <button
                        key={p}
                        style={{
                          ...styles.pageBtn,
                          ...(page === p ? styles.pageBtnActive : {}),
                        }}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    )
                  )}
                <button
                  style={{
                    ...styles.pageBtn,
                    opacity: page === totalPages ? 0.4 : 1,
                    cursor: page === totalPages ? 'default' : 'pointer',
                  }}
                  onClick={() => page < totalPages && setPage(page + 1)}
                  disabled={page === totalPages}
                >
                  <FiChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </motion.div>

      {/* ── Create / Edit Modal ──────────────────────────────────────────────── */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.overlay}
            onClick={closeModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.93, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.93, y: 20 }}
              transition={{ duration: 0.25 }}
              style={styles.modal}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeModal}
                style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  background: 'rgba(239, 68, 68, 0.08)',
                  border: 'none',
                  borderRadius: 8,
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#dc2626',
                }}
              >
                <FiX size={18} />
              </button>

              <div style={styles.modalTitle}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #059669, #10b981)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {editingContact ? (
                    <FiEdit2 size={18} color="white" />
                  ) : (
                    <FiPlus size={18} color="white" />
                  )}
                </div>
                {editingContact ? 'Edit Contact' : 'Create New Contact'}
              </div>

              <form onSubmit={handleSave}>
                <div style={styles.fieldGrid}>
                  {/* Contact Type */}
                  <div>
                    <label style={styles.fieldLabel}>Contact Type *</label>
                    <select
                      value={form.contact_type}
                      onChange={(e) => updateField('contact_type', e.target.value)}
                      style={styles.fieldSelect}
                    >
                      {CONTACT_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Name */}
                  <div>
                    <label style={styles.fieldLabel}>Name *</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      style={styles.fieldInput}
                      placeholder="Contact name"
                      required
                    />
                  </div>

                  {/* Deals In */}
                  <div>
                    <label style={styles.fieldLabel}>Deals In</label>
                    <input
                      type="text"
                      value={form.deals_in}
                      onChange={(e) => updateField('deals_in', e.target.value)}
                      style={styles.fieldInput}
                      placeholder="Products / commodities"
                    />
                  </div>

                  {/* Country */}
                  <div>
                    <label style={styles.fieldLabel}>Country</label>
                    <input
                      type="text"
                      value={form.country}
                      onChange={(e) => updateField('country', e.target.value)}
                      style={styles.fieldInput}
                      placeholder="Country"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label style={styles.fieldLabel}>Phone</label>
                    <input
                      type="text"
                      value={form.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      style={styles.fieldInput}
                      placeholder="Phone number"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label style={styles.fieldLabel}>Email</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      style={styles.fieldInput}
                      placeholder="email@example.com"
                    />
                  </div>

                  {/* Fax */}
                  <div>
                    <label style={styles.fieldLabel}>Fax</label>
                    <input
                      type="text"
                      value={form.fax}
                      onChange={(e) => updateField('fax', e.target.value)}
                      style={styles.fieldInput}
                      placeholder="Fax number"
                    />
                  </div>

                  {/* GSTIN */}
                  <div>
                    <label style={styles.fieldLabel}>GSTIN</label>
                    <input
                      type="text"
                      value={form.gstin}
                      onChange={(e) => updateField('gstin', e.target.value)}
                      style={styles.fieldInput}
                      placeholder="GST identification number"
                    />
                  </div>

                  {/* TIN */}
                  <div>
                    <label style={styles.fieldLabel}>TIN</label>
                    <input
                      type="text"
                      value={form.tin}
                      onChange={(e) => updateField('tin', e.target.value)}
                      style={styles.fieldInput}
                      placeholder="Tax identification number"
                    />
                  </div>

                  {/* CST No */}
                  <div>
                    <label style={styles.fieldLabel}>CST No</label>
                    <input
                      type="text"
                      value={form.cst_no}
                      onChange={(e) => updateField('cst_no', e.target.value)}
                      style={styles.fieldInput}
                      placeholder="CST number"
                    />
                  </div>

                  {/* Bank Account No */}
                  <div>
                    <label style={styles.fieldLabel}>Bank Account No</label>
                    <input
                      type="text"
                      value={form.bank_account_no}
                      onChange={(e) => updateField('bank_account_no', e.target.value)}
                      style={styles.fieldInput}
                      placeholder="Account number"
                    />
                  </div>

                  {/* Bank Name */}
                  <div>
                    <label style={styles.fieldLabel}>Bank Name</label>
                    <input
                      type="text"
                      value={form.bank_name}
                      onChange={(e) => updateField('bank_name', e.target.value)}
                      style={styles.fieldInput}
                      placeholder="Bank name"
                    />
                  </div>

                  {/* Bank IFSC */}
                  <div>
                    <label style={styles.fieldLabel}>Bank IFSC</label>
                    <input
                      type="text"
                      value={form.bank_ifsc}
                      onChange={(e) => updateField('bank_ifsc', e.target.value)}
                      style={styles.fieldInput}
                      placeholder="IFSC code"
                    />
                  </div>
                </div>

                {/* Address (full width) */}
                <div style={{ marginTop: 16 }}>
                  <label style={styles.fieldLabel}>Address</label>
                  <textarea
                    value={form.address}
                    onChange={(e) => updateField('address', e.target.value)}
                    style={styles.fieldTextarea}
                    placeholder="Full address"
                    rows={2}
                  />
                </div>

                {/* Remarks (full width) */}
                <div style={{ marginTop: 16 }}>
                  <label style={styles.fieldLabel}>Remarks</label>
                  <textarea
                    value={form.remarks}
                    onChange={(e) => updateField('remarks', e.target.value)}
                    style={styles.fieldTextarea}
                    placeholder="Additional notes"
                    rows={2}
                  />
                </div>

                {/* Buttons */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 12,
                    marginTop: 28,
                  }}
                >
                  <button type="button" style={styles.cancelBtn} onClick={closeModal}>
                    Cancel
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    type="submit"
                    style={{
                      ...styles.saveBtn,
                      opacity: saving ? 0.7 : 1,
                      cursor: saving ? 'not-allowed' : 'pointer',
                    }}
                    disabled={saving}
                  >
                    <FiSave size={16} />
                    {saving
                      ? 'Saving...'
                      : editingContact
                      ? 'Update Contact'
                      : 'Create Contact'}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Delete Confirmation Modal ─────────────────────────────────────────── */}
      <AnimatePresence>
        {deleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.overlay}
            onClick={() => setDeleteModal(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              style={{
                ...styles.modal,
                maxWidth: 440,
                textAlign: 'center',
                padding: '40px 32px',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: 16,
                  background: 'rgba(239, 68, 68, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                }}
              >
                <FiAlertTriangle size={28} color="#dc2626" />
              </div>
              <h3
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 20,
                  fontWeight: 700,
                  color: '#1f2937',
                  marginBottom: 8,
                }}
              >
                Delete Contact?
              </h3>
              <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 28 }}>
                Are you sure you want to delete{' '}
                <strong style={{ color: '#064e3b' }}>{deleteModal.name}</strong>?
                This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <button
                  style={styles.cancelBtn}
                  onClick={() => setDeleteModal(null)}
                >
                  Cancel
                </button>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  style={{
                    ...styles.saveBtn,
                    background: 'linear-gradient(135deg, #dc2626, #ef4444)',
                    boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)',
                  }}
                  onClick={handleDelete}
                >
                  <FiTrash2 size={16} />
                  Delete
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── PDF Preview Modal ─────────────────────────────────────────────────── */}
      <AnimatePresence>
        {pdfPreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.75)',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
              padding: '20px',
            }}
            onClick={closePdfPreview}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 30 }}
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              style={{
                background: 'linear-gradient(to bottom, #ffffff, #fafafa)',
                borderRadius: 16,
                maxWidth: '95vw',
                width: 1000,
                maxHeight: '92vh',
                height: '88vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 24px 48px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.08)',
                position: 'relative',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Enhanced Header */}
              <div
                style={{
                  background: 'linear-gradient(135deg, #047857 0%, #059669 100%)',
                  padding: '20px 28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                    }}
                  >
                    <FiUsers size={22} color="#ffffff" />
                  </div>
                  <div>
                    <h3
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: 20,
                        fontWeight: 700,
                        color: '#ffffff',
                        margin: 0,
                        marginBottom: 4,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {pdfPreview.contact.name || 'Contact'}
                    </h3>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 13,
                        color: 'rgba(255, 255, 255, 0.85)',
                        fontWeight: 500,
                      }}
                    >
                      👤 Contact Details
                      {pdfPreview.contact.contact_type && ` • ${pdfPreview.contact.contact_type}`}
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      window.open(pdfPreview.url, '_blank');
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 18px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: 10,
                      color: '#ffffff',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'Inter, sans-serif',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                    }}
                    title="Print PDF"
                  >
                    <FiEye size={16} />
                    Print
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = pdfPreview.url;
                      link.download = `Contact_${(pdfPreview.contact.name || 'Unknown').replace(/\s+/g, '_')}.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      toast.success('PDF downloaded successfully!');
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 18px',
                      background: '#ffffff',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      borderRadius: 10,
                      color: '#047857',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'Inter, sans-serif',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                    }}
                    title="Download PDF"
                  >
                    <FiDownload size={16} />
                    Download
                  </motion.button>

                  <button
                    onClick={closePdfPreview}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(10px)',
                      color: '#ffffff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(239, 68, 68, 0.9)';
                      e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.9)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                    }}
                    title="Close"
                  >
                    <FiX size={20} />
                  </button>
                </div>
              </div>

              {/* PDF Viewer Area */}
              <div
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  background: '#e5e7eb',
                  position: 'relative',
                }}
              >
                <iframe
                  src={`${pdfPreview.url}#toolbar=1&navpanes=0&scrollbar=1&view=FitH`}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  title="PDF Preview"
                />
              </div>

              {/* Footer Info */}
              <div
                style={{
                  padding: '12px 28px',
                  background: '#f9fafb',
                  borderTop: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: 12,
                  color: '#6b7280',
                }}
              >
                <span style={{ fontWeight: 500 }}>
                  📅 {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
                <span>
                  💼 DEY INTL. Export Management
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── CSV Import Modal ───────────────────────────────────────────────────── */}
      <AnimatePresence>
        {importModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.overlay}
            onClick={() => {
              if (!importing) {
                setImportModal(false);
                setImportFile(null);
                setUploadProgress(0);
              }
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.93, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.93, y: 20 }}
              transition={{ duration: 0.25 }}
              style={{
                ...styles.modal,
                maxWidth: 540,
                textAlign: 'center',
                padding: '40px 32px',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => {
                  if (!importing) {
                    setImportModal(false);
                    setImportFile(null);
                    setUploadProgress(0);
                  }
                }}
                style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  background: 'rgba(239, 68, 68, 0.08)',
                  border: 'none',
                  borderRadius: 8,
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: importing ? 'not-allowed' : 'pointer',
                  color: '#dc2626',
                  opacity: importing ? 0.5 : 1,
                }}
              >
                <FiX size={18} />
              </button>

              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: 16,
                  background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                }}
              >
                <FiUpload size={28} color="#ffffff" />
              </div>

              <h3
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 20,
                  fontWeight: 700,
                  color: '#1f2937',
                  marginBottom: 8,
                }}
              >
                Import Contacts from CSV
              </h3>

              <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 12 }}>
                Upload a CSV file with contact data. The file should have columns: name, deals in, address, phone, email, remarks
              </p>

              <button
                onClick={downloadDemoCSV}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#2563eb',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  margin: '0 auto 20px',
                  padding: '6px 12px',
                  borderRadius: 6,
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(37, 99, 235, 0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <FiDownload size={14} />
                Download Sample CSV Template
              </button>

              <div
                style={{
                  border: '2px dashed rgba(37, 99, 235, 0.3)',
                  borderRadius: 12,
                  padding: '32px 24px',
                  background: 'rgba(37, 99, 235, 0.05)',
                  marginBottom: 24,
                }}
              >
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      background: 'rgba(37, 99, 235, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 12,
                    }}
                  >
                    <FiUpload size={24} color="#2563eb" />
                  </div>
                  <p
                    style={{
                      fontWeight: 600,
                      color: '#2563eb',
                      marginBottom: 4,
                      fontSize: 15,
                    }}
                  >
                    Click to select CSV file
                  </p>
                  {importFile && (
                    <p
                      style={{
                        color: '#059669',
                        fontSize: 13,
                        marginTop: 8,
                        fontWeight: 500,
                      }}
                    >
                      ✓ {importFile.name}
                    </p>
                  )}
                </label>
              </div>

              {/* Upload Progress Bar */}
              {importing && uploadProgress > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      width: '100%',
                      height: 8,
                      background: 'rgba(37, 99, 235, 0.1)',
                      borderRadius: 4,
                      overflow: 'hidden',
                      marginBottom: 8,
                    }}
                  >
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${uploadProgress}%` }}
                      transition={{ duration: 0.3 }}
                      style={{
                        height: '100%',
                        background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                        borderRadius: 4,
                      }}
                    />
                  </div>
                  <p
                    style={{
                      fontSize: 13,
                      color: '#2563eb',
                      fontWeight: 600,
                      textAlign: 'center',
                    }}
                  >
                    {uploadProgress === 100 ? 'Processing...' : `Uploading: ${uploadProgress}%`}
                  </p>
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <button
                  style={styles.cancelBtn}
                  onClick={() => {
                    setImportModal(false);
                    setImportFile(null);
                    setUploadProgress(0);
                  }}
                  disabled={importing}
                >
                  Cancel
                </button>
                <motion.button
                  whileHover={{ scale: importing ? 1 : 1.03 }}
                  whileTap={{ scale: importing ? 1 : 0.97 }}
                  style={{
                    ...styles.saveBtn,
                    background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                    boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
                    opacity: importing || !importFile ? 0.7 : 1,
                    cursor: importing || !importFile ? 'not-allowed' : 'pointer',
                  }}
                  onClick={handleImport}
                  disabled={importing || !importFile}
                >
                  <FiUpload size={16} />
                  {importing
                    ? (uploadProgress === 100 ? 'Processing...' : `Uploading ${uploadProgress}%`)
                    : 'Import Contacts'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
